// SsoCodeSheet — s95. The second step of a Google/Apple sign-in for an account that has
// two-factor turned on. The code goes to the server with the short-lived challenge from
// the first call (useSsoSignIn.submitCode); a backup code is accepted the same way.
import { useEffect, useRef, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { useI18n } from '../i18n'
import { OverlayPortal } from './ModalPortal'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'

export default function SsoCodeSheet({ step, busy, error, onSubmit, onCancel }) {
  const { t } = useI18n()
  const [value, setValue] = useState('')
  const [backup, setBackup] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => { setValue(''); setBackup(false) }, [step])
  useEffect(() => { if (step) setTimeout(() => inputRef.current?.focus(), 150) }, [step, backup])

  if (!step) return null
  const ready = backup ? value.trim().length >= 6 : /^\d{6}$/.test(value)
  const submit = (e) => {
    e.preventDefault()
    if (!ready || busy) return
    onSubmit(value.trim(), { backup })
  }
  const hint = backup
    ? t('ssoCode.backupHint', 'Enter one of the backup codes you saved when you set up two-factor.')
    : step.method === 'sms' && !step.smsRefused
      ? t('ssoCode.smsHint', 'We texted a 6-digit code to the mobile on your account.')
      : t('ssoCode.totpHint', 'Open your authenticator app and enter the 6-digit code for Orella.')

  return (
    <OverlayPortal>
      <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.45)' }}>
        <form onSubmit={submit} className="bg-white w-full max-w-[420px] rounded-t-3xl sm:rounded-3xl"
          style={{ padding: '24px 22px', paddingBottom: 'calc(22px + env(safe-area-inset-bottom, 0px))' }}>
          <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
            <ShieldCheck size={22} color={BLUE} />
            <h3 style={{ fontSize: 19, fontWeight: 700, color: NAVY, margin: 0 }}>
              {t('ssoCode.title', 'Enter your verification code')}
            </h3>
          </div>
          <p style={{ fontSize: 14.5, color: '#4A5A6B', lineHeight: 1.5, marginBottom: 16 }}>{hint}</p>
          <input
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(backup ? e.target.value : e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode={backup ? 'text' : 'numeric'}
            autoComplete="one-time-code"
            aria-label={t('ssoCode.title', 'Enter your verification code')}
            className="w-full"
            style={{ height: 54, borderRadius: 14, border: '1.5px solid #E5EDF3', padding: '0 16px', fontSize: 20,
              letterSpacing: backup ? 1 : 6, color: NAVY, fontVariantNumeric: 'tabular-nums' }}
          />
          {error && <p role="alert" style={{ fontSize: 13, color: '#DC2626', marginTop: 8 }}>{error}</p>}
          <button type="submit" disabled={!ready || busy} className="w-full flex items-center justify-center gap-2"
            style={{ height: 54, marginTop: 16, borderRadius: 16, border: 0, background: ready ? BLUE : '#CFE3F3',
              color: '#fff', fontSize: 16.5, fontWeight: 700 }}>
            {busy ? <OrellaSpinner size={18} tone="inherit" /> : null}
            {t('ssoCode.verify', 'Verify and sign in')}
          </button>
          <div className="flex justify-between" style={{ marginTop: 14 }}>
            <button type="button" onClick={() => { setBackup(!backup); setValue('') }}
              style={{ background: 'none', border: 0, color: BLUE, fontSize: 14, fontWeight: 600 }}>
              {backup ? t('ssoCode.useCode', 'Use a 6-digit code') : t('ssoCode.useBackup', 'Use a backup code')}
            </button>
            <button type="button" onClick={onCancel} disabled={busy}
              style={{ background: 'none', border: 0, color: '#8598A9', fontSize: 14, fontWeight: 600 }}>
              {t('common.cancel', 'Cancel')}
            </button>
          </div>
        </form>
      </div>
    </OverlayPortal>
  )
}
